import { AdditionalLayoutProperties, layoutDataItemToCss } from '@wix/thunderbolt-becky-root'
import { Component, LayoutDataItems } from '@wix/thunderbolt-becky-types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import _, { get, identity, isUndefined, mapValues, merge, set } from 'lodash'
import { createCssCompNode } from '../cssCompNode'
import { FeatureRefs } from '../cssFeatures.types'
import { BreakpointsVariantsToLayoutsMap, BreakpointsVariantsToSelectorsMap } from './responsiveLayout.types'
import { clearFutileOverridesFromSelectorsMap } from '../../utils/futileCssOptimizer'
import { EMPTY_ARRAY } from '../../utils/constants'
import { getContainerLayouts } from '../../utils/responsiveLayoutUtils'

const ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS = new Set(['Container', 'Section'])

const CONTAINER_SELECTORS = ['component-one-cell-grid', 'container']

const COMPONENT_DEFAULT_CSS_PATH = ['default', '', 'component']
const ITEM_DEFAULT_CSS_PATH = ['default', '', 'item']
const PATHS = {
	POSITION: [...COMPONENT_DEFAULT_CSS_PATH, 'position'],
	ASPECT_RATIO: [...COMPONENT_DEFAULT_CSS_PATH, 'aspect-ratio'],
	DISPLAY: [...COMPONENT_DEFAULT_CSS_PATH, 'display'],
	TOP: [...COMPONENT_DEFAULT_CSS_PATH, 'top'],
	COMPONENT_DISPLAY: [...COMPONENT_DEFAULT_CSS_PATH, '--comp-display'],
	TOP_VARIABLE: [...ITEM_DEFAULT_CSS_PATH, '--top'],
}

const variablize = (variable: string, fallback?: string | number) =>
	typeof fallback !== 'undefined' ? `var(${variable},${fallback})` : `var(${variable})`

export const VARIABLES = {
	DISPLAY: '--display',
	CONTAINER_DISPLAY: '--container-display',
	LAYOUT_DISPLAY: '--l_display',
	ASPECT_RATIO: '--aspect-ratio',
	TOP: '--top',
	IS_STICKY: '--is-sticky',
	WIX_ADS_TOP_HEIGHT: '--wix-ads-height',
	STICKY_OFFSET: '--sticky-offset',
	Z_INDEX_OVERRIDE: '--z-index-override',
} as const

const MOBILE_VARIANT_ID = 'MOBILE-VARIANT'

const CONTAINER_DISPLAY = variablize(VARIABLES.LAYOUT_DISPLAY, variablize(VARIABLES.CONTAINER_DISPLAY))
const COMPONENT_DEFAULTS = {
	TOP: `calc(${variablize(VARIABLES.IS_STICKY)} * (${variablize(VARIABLES.TOP)} + ${variablize(
		VARIABLES.STICKY_OFFSET
	)}))`,
	DISPLAY: variablize(VARIABLES.LAYOUT_DISPLAY, variablize(VARIABLES.DISPLAY)),
	ASPECT_RATIO: `1/${variablize(VARIABLES.ASPECT_RATIO)}`,
	COMPONENT_DISPLAY: 'unset',
}
const WILL_CHANGE_OPACITY = { 'will-change': 'opacity' }
const COMPONENT_DEFAULT_BEFORE_CSS_PATH = ['default', '', '::before']
const BEFORE_PATHS = {
	CONTENT: [...COMPONENT_DEFAULT_BEFORE_CSS_PATH, 'content'],
	DISPLAY: [...COMPONENT_DEFAULT_BEFORE_CSS_PATH, 'display'],
	PADDING_TOP: [...COMPONENT_DEFAULT_BEFORE_CSS_PATH, 'padding-top'],
}
// ::before is for the psudo element way of aspect ratio
const getAspectRatioBeforePath = (shouldUseNativeAspectRatio: boolean) => [
	shouldUseNativeAspectRatio ? 'component' : '::before',
	VARIABLES.ASPECT_RATIO,
]

const applyAspectRatio = (css: BreakpointsVariantsToSelectorsMap, shouldUseNativeAspectRatio: boolean) => {
	for (const breakpointId in css) {
		const breakpointCss = css[breakpointId]
		for (const variantsKey in breakpointCss) {
			const variantCss = breakpointCss[variantsKey]
			const aspectRatio = get(variantCss, getAspectRatioBeforePath(shouldUseNativeAspectRatio))
			const componentHeight = get(variantCss, ['component', 'height'])
			// use auto aspect ratio if no aspect ratio is set or if the component height is set
			if (isUndefined(aspectRatio) && !isUndefined(componentHeight)) {
				set(variantCss, getAspectRatioBeforePath(shouldUseNativeAspectRatio), 'auto')
			}
		}
	}

	// replace these three lines with the following one when aspect-ratio is mostly supported
	if (shouldUseNativeAspectRatio) {
		set(css, PATHS.ASPECT_RATIO, COMPONENT_DEFAULTS.ASPECT_RATIO)
	} else {
		set(css, BEFORE_PATHS.CONTENT, 'attr(x)')
		set(css, BEFORE_PATHS.DISPLAY, 'block')
		set(css, BEFORE_PATHS.PADDING_TOP, `calc(${variablize(VARIABLES.ASPECT_RATIO)} * 100%)`)
	}
}

const isClassicLayout = (breakpointToLayouts: BreakpointsVariantsToLayoutsMap) =>
	Object.values(breakpointToLayouts)
		.flatMap((x) => Object.values(x).flatMap((y) => y))
		.some((layout) => layout.type === 'ClassicComponentLayout')

const filterVariantInCaseOfClassic = (
	variantInSpecificBreakpoint: { [p: string]: Array<LayoutDataItems> },
	isMobileView: boolean
): { [p: string]: Array<LayoutDataItems> } =>
	isMobileView && variantInSpecificBreakpoint[MOBILE_VARIANT_ID]
		? // We don't want to apply .device-mobile-optimized class because it'll override the css reaching from css mappers
		  { '': variantInSpecificBreakpoint[MOBILE_VARIANT_ID] }
		: _.omit(variantInSpecificBreakpoint, [MOBILE_VARIANT_ID])

const responsiveLayoutCompNode = createCssCompNode('responsiveLayout', 'responsiveLayout', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) =>
		component.layoutQuery
			? {
					breakpointsVariantsToLayouts: refs.compLayouts(component),
					layoutFlags: refs.compLayoutFlags(component),
					renderScrollSnap: envRefs.renderScrollSnap,
					globalRenderSticky: envRefs.renderSticky,
					componentRenderSticky: envRefs.compsToRenderSticky(component.id),
					editorName: envRefs.editorName,
					isMobileView: envRefs.isMobileView,
					experiments: envRefs.experiments,
					childrenIsPinned: (
						(ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS.has(component.componentType) && component.components) ||
						EMPTY_ARRAY
					).map(refs.isPinned),
			  }
			: null,
	toViewItem: (component, deps) => {
		if (!deps) {
			return null
		}

		const {
			breakpointsVariantsToLayouts,
			renderScrollSnap,
			globalRenderSticky,
			componentRenderSticky,
			experiments,
			editorName,
			childrenIsPinned,
			isMobileView,
			layoutFlags,
		} = deps

		if (!breakpointsVariantsToLayouts || !layoutFlags) {
			return { css: {}, shouldOmitWrapperLayers: false }
		}

		const isMesh2ExperimentOpen = !!experiments['specs.thunderbolt.mesh2']

		const shouldUseNativeAspectRatio = editorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']

		const containerLayouts = getContainerLayouts(breakpointsVariantsToLayouts)

		const hasClassicLayout = isClassicLayout(breakpointsVariantsToLayouts)

		const isOneCellGridDisplayBlock =
			!!experiments['specs.thunderbolt.one_cell_grid_display_block'] && !childrenIsPinned.some(identity)
		const isOneCellGridDisplayFlex =
			!isOneCellGridDisplayBlock &&
			!!experiments['specs.thunderbolt.one_cell_grid_display_flex'] &&
			ONE_CELL_GRID_DISPLAY_FLEX_COMPONENTS.has(component.componentType) &&
			!childrenIsPinned.some(identity)
		const options: AdditionalLayoutProperties = {
			hasOverflow: layoutFlags.hasOverflow,
			shouldOmitWrapperLayers: layoutFlags.shouldOmitWrapperLayers,
			renderScrollSnap,
			renderSticky: globalRenderSticky || !!componentRenderSticky,
			isOneCellGridDisplayFlex,
			isOneCellGridDisplayBlock,
		}

		let hasAspectRatioSomewhere = false
		const css = mapValues(breakpointsVariantsToLayouts, (variantInSpecificBreakpoint, breakpointId) => {
			return mapValues(
				isMesh2ExperimentOpen
					? filterVariantInCaseOfClassic(variantInSpecificBreakpoint, isMobileView)
					: variantInSpecificBreakpoint,
				(layouts, variantId) => {
					const itemsCss = layouts.map((layout) =>
						layoutDataItemToCss(layout, {
							...options,
							spx: get(breakpointsVariantsToLayouts, [breakpointId, '', '0', 'spx']),
						})
					)
					const mergedCss = merge({}, ...itemsCss)

					// assert default position
					if (!mergedCss?.item?.position && breakpointId === 'default' && variantId === '') {
						set(mergedCss, ['item', 'position'], 'relative')
					}

					const aspectRatio = mergedCss?.component?.[VARIABLES.ASPECT_RATIO]
					if (aspectRatio) {
						hasAspectRatioSomewhere = true
						if (!shouldUseNativeAspectRatio) {
							set(mergedCss, getAspectRatioBeforePath(shouldUseNativeAspectRatio), aspectRatio)
							delete mergedCss.component[VARIABLES.ASPECT_RATIO]
						}
					}

					if (!hasClassicLayout) {
						for (const selector of CONTAINER_SELECTORS) {
							if (mergedCss?.[selector]?.display) {
								mergedCss[selector][VARIABLES.CONTAINER_DISPLAY] = mergedCss[selector].display
								mergedCss[selector].display = CONTAINER_DISPLAY
							}
						}

						const position = mergedCss?.item?.position
						if (position) {
							mergedCss.item[VARIABLES.IS_STICKY] = position === 'sticky' ? 1 : 0
						}
						if (position === 'sticky') {
							// https://jira.wixpress.com/browse/LAYOUT-441
							mergedCss['next-siblings'] = {
								...WILL_CHANGE_OPACITY,
								[VARIABLES.Z_INDEX_OVERRIDE]: 1,
							}

							mergedCss.item[VARIABLES.Z_INDEX_OVERRIDE] = 1
						}
					}

					return mergedCss
				}
			)
		})

		if (hasAspectRatioSomewhere) {
			applyAspectRatio(css, shouldUseNativeAspectRatio)
		}

		if (!hasClassicLayout) {
			set(css, PATHS.TOP, COMPONENT_DEFAULTS.TOP)

			// set top variables to 0px if don't exist on default breakpoint so it won't cascade from parent
			if (isUndefined(get(css, PATHS.TOP_VARIABLE))) {
				set(css, PATHS.TOP_VARIABLE, '0px')
			}

			if (isUndefined(get(css, PATHS.COMPONENT_DISPLAY))) {
				set(css, PATHS.COMPONENT_DISPLAY, COMPONENT_DEFAULTS.COMPONENT_DISPLAY)
			}

			// allow leaf components to specify their own display via css variable
			if (!containerLayouts.length) {
				set(css, PATHS.DISPLAY, COMPONENT_DEFAULTS.DISPLAY)
			}
		}

		return {
			css,
			shouldOmitWrapperLayers: layoutFlags.shouldOmitWrapperLayers,
			isMobileView,
		}
	},
})

export const viewerResponsiveLayoutCompNode = createCssCompNode('responsiveLayout', 'responsiveLayout', {
	getDependencies: responsiveLayoutCompNode.getDependencies,
	toViewItem: (component, deps) => {
		const result = responsiveLayoutCompNode.toViewItem(component, deps)
		if (!result) {
			return null
		}
		const { css, shouldOmitWrapperLayers, isMobileView } = result
		return {
			css: clearFutileOverridesFromSelectorsMap(css),
			shouldOmitWrapperLayers,
			isMobileView,
		}
	},
})

export const responsiveLayout = (isEditor: boolean) =>
	isEditor ? responsiveLayoutCompNode : viewerResponsiveLayoutCompNode
